/**
 * Where the strings come from.
 *
 * `catalogues/<dir>/<page>.json`, one directory per language (`de`) and,
 * sparsely, per locale that overrides its language (`en-us` for spelling).
 * Each file holds the keys of one page or shared component, prefixed with its
 * own name (`pricing.hero.title` lives in `pricing.json`), so two people — or
 * two agents — extracting different pages never touch the same file.
 *
 * English is bundled eagerly: it is the source language and the fallback for
 * every key, so the first paint never waits on it. Every other directory is a
 * lazy chunk per file, fetched only for the language being read. The two globs
 * are disjoint so Vite does not emit the English files twice.
 */

import type { Catalogue } from './types';

const EAGER = import.meta.glob('./catalogues/en/*.json', {
  eager: true,
  import: 'default'
}) as Record<string, Catalogue>;

const LAZY = import.meta.glob(['./catalogues/*/*.json', '!./catalogues/en/*.json'], {
  import: 'default'
}) as Record<string, () => Promise<Catalogue>>;

const merge = (parts: Catalogue[]): Catalogue => Object.assign({}, ...parts);

/** The source language, whole. */
export const EN: Catalogue = merge(Object.values(EAGER));

const dirOf = (dir: string) => `./catalogues/${dir}/`;

/** Whether any file exists for a language or locale directory. */
export function hasCatalogue(dir: string): boolean {
  const prefix = dirOf(dir);
  return Object.keys(LAZY).some((k) => k.startsWith(prefix));
}

const cache = new Map<string, Promise<Catalogue>>();

/**
 * Every file of one directory, merged. Cached per directory, and a file that
 * fails to load contributes nothing rather than failing the language — the
 * English fallback covers its keys.
 */
export function loadCatalogue(dir: string): Promise<Catalogue> {
  let pending = cache.get(dir);
  if (!pending) {
    const prefix = dirOf(dir);
    const loaders = Object.entries(LAZY)
      .filter(([k]) => k.startsWith(prefix))
      .map(([, load]) => load);
    pending = Promise.all(loaders.map((load) => load().catch(() => ({}) as Catalogue))).then(merge);
    cache.set(dir, pending);
  }
  return pending;
}
